import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Printer, FileText, AlertTriangle } from 'lucide-react';
import api from '../api/client';

export default function AccountingLedgerPage() {
  const [tab, setTab] = useState<'ledger'|'inventory'>('ledger');

  const { data: ledgerData } = useQuery({
    queryKey: ['ledger'],
    queryFn: () => api.get('/acquisitions/ledger'),
    enabled: tab === 'ledger'
  } as any);

  const { data: invData } = useQuery({
    queryKey: ['inventory-report'],
    queryFn: () => api.get('/acquisitions/inventory-report'),
    enabled: tab === 'inventory'
  } as any);

  const ledger = (ledgerData as any)?.data?.ledger || [];
  const items = (invData as any)?.data?.items || [];

  const totalValue = ledger.reduce((s: number, r: any) => s + Number(r.total_value||0), 0);
  const totalCopies = ledger.reduce((s: number, r: any) => s + Number(r.total_copies||0), 0);

  const handlePrintLedger = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><title>საბუღალტრო ნუსხა</title>
      <style>
        body{font-family:sans-serif;padding:20px;font-size:12px}
        h1{color:#1d4ed8;font-size:16px;border-bottom:2px solid #1d4ed8;padding-bottom:8px}
        table{width:100%;border-collapse:collapse;margin-top:12px}
        th{background:#f1f5f9;padding:8px;text-align:left;font-size:11px;border:1px solid #e2e8f0}
        td{padding:7px 8px;border:1px solid #e2e8f0}
        .total{background:#eff6ff;font-weight:700}
        tfoot td{background:#dbeafe;font-weight:700}
        .footer{text-align:center;font-size:10px;color:#94a3b8;margin-top:16px}
        @media print{body{padding:4px}}
      </style></head><body>
      <h1>📚 Folios — საბუღალტრო ნუსხა</h1>
      <p style="color:#64748b;font-size:11px">გენერირებულია: ${new Date().toLocaleString('ka-GE')}</p>
      <table>
        <thead><tr>
          <th>ანგარიში</th><th>დასახელება</th><th>სათაური</th>
          <th>სულ ასლი</th><th>ხელმისაწვდომი</th><th>გაცემული</th><th>ჯამური ღირებულება</th>
        </tr></thead>
        <tbody>
          ${ledger.map((r: any) => `<tr>
            <td style="font-family:monospace">${r.code}</td>
            <td>${r.name}</td>
            <td>${r.book_titles||0}</td>
            <td>${r.total_copies||0}</td>
            <td>${r.available_copies||0}</td>
            <td>${r.checked_out_copies||0}</td>
            <td>₾${Number(r.total_value||0).toFixed(2)}</td>
          </tr>`).join('')}
        </tbody>
        <tfoot><tr>
          <td colspan="3">სულ:</td>
          <td>${totalCopies}</td>
          <td></td><td></td>
          <td>₾${totalValue.toFixed(2)}</td>
        </tr></tfoot>
      </table>
      <div class="footer">© 2026 Kakhaber Revazishvili · SANGU</div>
      </body></html>`);
    win.document.close(); win.print();
  };

  const handlePrintInventory = () => {
    const win = window.open('', '_blank');
    if (!win) return;
    const discrepancies = items.filter((i: any) => Number(i.lost) > 0);
    win.document.write(`
      <html><head><title>ინვენტარიზაციის ანგარიში</title>
      <style>
        body{font-family:sans-serif;padding:20px;font-size:11px}
        h1{color:#1d4ed8;font-size:15px;border-bottom:2px solid #1d4ed8;padding-bottom:8px}
        table{width:100%;border-collapse:collapse;margin-top:10px}
        th{background:#f1f5f9;padding:6px 8px;text-align:left;font-size:10px;border:1px solid #e2e8f0}
        td{padding:5px 8px;border:1px solid #e2e8f0}
        .loss{background:#fff1f2;color:#dc2626}
        tfoot td{background:#dbeafe;font-weight:700}
        .footer{text-align:center;font-size:10px;color:#94a3b8;margin-top:12px}
      </style></head><body>
      <h1>📚 Folios — ინვენტარიზაციის შემაჯამებელი ანგარიში</h1>
      <p style="color:#64748b;font-size:10px">გენერირებულია: ${new Date().toLocaleString('ka-GE')}</p>
      ${discrepancies.length > 0 ? `<p style="color:#dc2626;font-weight:600">⚠ დანაკლისი: ${discrepancies.length} სახელწოდება</p>` : '<p style="color:#16a34a">✓ დანაკლისი არ არის</p>'}
      <table>
        <thead><tr>
          <th>ანგარიში</th><th>ISBN</th><th>სათაური</th><th>ავტორი</th>
          <th>ფასი</th><th>სულ</th><th>ხელმისაწვდ.</th><th>გაცემული</th><th>დანაკლისი</th><th>ღირებულება</th>
        </tr></thead>
        <tbody>
          ${items.map((r: any) => `<tr ${Number(r.lost)>0?'class="loss"':''}>
            <td style="font-family:monospace">${r.account_code||'—'}</td>
            <td style="font-family:monospace">${r.isbn||'—'}</td>
            <td>${r.title}</td>
            <td>${r.author||'—'}</td>
            <td>₾${Number(r.acquisition_price||0).toFixed(2)}</td>
            <td>${r.total_copies||0}</td>
            <td>${r.available||0}</td>
            <td>${r.checked_out||0}</td>
            <td ${Number(r.lost)>0?'style="color:#dc2626;font-weight:700"':''}>${r.lost||0}</td>
            <td>₾${Number(r.total_value||0).toFixed(2)}</td>
          </tr>`).join('')}
        </tbody>
        <tfoot><tr>
          <td colspan="5">სულ:</td>
          <td>${items.reduce((s: number, i: any) => s + Number(i.total_copies||0), 0)}</td>
          <td>${items.reduce((s: number, i: any) => s + Number(i.available||0), 0)}</td>
          <td>${items.reduce((s: number, i: any) => s + Number(i.checked_out||0), 0)}</td>
          <td>${items.reduce((s: number, i: any) => s + Number(i.lost||0), 0)}</td>
          <td>₾${items.reduce((s: number, i: any) => s + Number(i.total_value||0), 0).toFixed(2)}</td>
        </tr></tfoot>
      </table>
      <div class="footer">© 2026 Kakhaber Revazishvili · SANGU</div>
      </body></html>`);
    win.document.close(); win.print();
  };

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:600,color:'#f1f5f9',margin:0}}>საბუღალტრო ნუსხა</h1>
          <p style={{fontSize:12,color:'#475569',marginTop:4}}>ბიბლიოთეკური ფონდის საბუღალტრო აღრიცხვა</p>
        </div>
        <button onClick={tab==='ledger'?handlePrintLedger:handlePrintInventory}
          style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#111827',border:'1px solid #1e2d45',borderRadius:8,color:'#60a5fa',fontSize:12,cursor:'pointer'}}>
          <Printer size={14}/> ბეჭდვა
        </button>
      </div>

      <div style={{display:'flex',gap:4,marginBottom:16,borderBottom:'1px solid #1e2d45'}}>
        {[{key:'ledger',label:'საბუღალტრო ნუსხა'},{key:'inventory',label:'ინვენტარიზაციის ანგარიში'}].map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            style={{padding:'8px 16px',background:'none',border:'none',borderBottom:tab===t.key?'2px solid #60a5fa':'2px solid transparent',marginBottom:-1,fontSize:13,color:tab===t.key?'#60a5fa':'#475569',cursor:'pointer',fontWeight:tab===t.key?500:400}}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'ledger' && (
        <>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:16}}>
            {[
              {label:'ანგარიშების რაოდენობა', val:ledger.length, color:'#60a5fa'},
              {label:'სულ ასლი', val:totalCopies.toLocaleString(), color:'#34d399'},
              {label:'სულ ღირებულება', val:`₾${totalValue.toFixed(2)}`, color:'#fbbf24'},
            ].map(s => (
              <div key={s.label} style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:10,padding:14}}>
                <div style={{fontSize:11,color:'#475569',marginBottom:4}}>{s.label}</div>
                <div style={{fontSize:20,fontWeight:700,color:s.color}}>{s.val}</div>
              </div>
            ))}
          </div>

          <div style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead>
                <tr style={{borderBottom:'1px solid #1e2d45'}}>
                  {['ანგარიში','დასახელება','სათაური','სულ ასლი','ხელმისაწვდ.','გაცემული','ჯამური ღირებ.'].map(h => (
                    <th key={h} style={{textAlign:'left',padding:'10px 16px',fontSize:11,fontWeight:500,color:'#475569'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ledger.length === 0 && (
                  <tr><td colSpan={7} style={{padding:32,textAlign:'center',color:'#334155',fontSize:13}}>
                    ჩანაწერი არ არის — წიგნებს საბუღალტრო ანგარიში მიანიჭეთ
                  </td></tr>
                )}
                {ledger.map((r: any) => (
                  <tr key={r.code} style={{borderBottom:'1px solid #0f1826'}}>
                    <td style={{padding:'10px 16px',fontFamily:'monospace',color:'#60a5fa',fontWeight:500}}>{r.code}</td>
                    <td style={{padding:'10px 16px',color:'#cbd5e1'}}>{r.name}</td>
                    <td style={{padding:'10px 16px',color:'#64748b'}}>{r.book_titles||0}</td>
                    <td style={{padding:'10px 16px',color:'#f1f5f9',fontWeight:600}}>{r.total_copies||0}</td>
                    <td style={{padding:'10px 16px',color:'#34d399'}}>{r.available_copies||0}</td>
                    <td style={{padding:'10px 16px',color:'#fbbf24'}}>{r.checked_out_copies||0}</td>
                    <td style={{padding:'10px 16px',color:'#fbbf24',fontWeight:600}}>₾{Number(r.total_value||0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              {ledger.length > 0 && (
                <tfoot>
                  <tr style={{borderTop:'2px solid #1e2d45',background:'#0f1826'}}>
                    <td colSpan={3} style={{padding:'10px 16px',fontSize:12,fontWeight:600,color:'#94a3b8'}}>სულ:</td>
                    <td style={{padding:'10px 16px',fontWeight:700,color:'#f1f5f9'}}>{totalCopies}</td>
                    <td style={{padding:'10px 16px',fontWeight:700,color:'#34d399'}}>{ledger.reduce((s: number,r: any)=>s+Number(r.available_copies||0),0)}</td>
                    <td style={{padding:'10px 16px',fontWeight:700,color:'#fbbf24'}}>{ledger.reduce((s: number,r: any)=>s+Number(r.checked_out_copies||0),0)}</td>
                    <td style={{padding:'10px 16px',fontWeight:700,color:'#fbbf24'}}>₾{totalValue.toFixed(2)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}

      {tab === 'inventory' && (
        <div style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
          {items.length === 0 && (
            <div style={{padding:48,textAlign:'center'}}>
              <FileText size={40} style={{color:'#1e2d45',margin:'0 auto 12px',display:'block'}} />
              <p style={{color:'#334155',fontSize:13}}>ინვენტარიზაციის ჩანაწერი არ არის</p>
              <p style={{color:'#1e2d45',fontSize:11,marginTop:4}}>წიგნებს საბუღალტრო ანგარიში მიანიჭეთ</p>
            </div>
          )}
          {items.length > 0 && (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{borderBottom:'1px solid #1e2d45'}}>
                  {['ანგარიში','ISBN','სათაური','ავტორი','ფასი','სულ','ხელმ.','გაცემ.','დანაკლ.','ღირებ.'].map(h => (
                    <th key={h} style={{textAlign:'left',padding:'9px 12px',fontSize:10,fontWeight:500,color:'#475569'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((r: any) => (
                  <tr key={r.id} style={{borderBottom:'1px solid #0f1826',background:Number(r.lost)>0?'#2d0a0a22':'transparent'}}>
                    <td style={{padding:'8px 12px',fontFamily:'monospace',color:'#60a5fa',fontSize:11}}>{r.account_code||'—'}</td>
                    <td style={{padding:'8px 12px',fontFamily:'monospace',color:'#475569',fontSize:10}}>{r.isbn||'—'}</td>
                    <td style={{padding:'8px 12px',color:'#cbd5e1',maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.title}</td>
                    <td style={{padding:'8px 12px',color:'#64748b'}}>{r.author||'—'}</td>
                    <td style={{padding:'8px 12px',color:'#64748b'}}>₾{Number(r.acquisition_price||0).toFixed(2)}</td>
                    <td style={{padding:'8px 12px',color:'#f1f5f9',fontWeight:600}}>{r.total_copies||0}</td>
                    <td style={{padding:'8px 12px',color:'#34d399'}}>{r.available||0}</td>
                    <td style={{padding:'8px 12px',color:'#fbbf24'}}>{r.checked_out||0}</td>
                    <td style={{padding:'8px 12px',fontWeight:Number(r.lost)>0?700:400,color:Number(r.lost)>0?'#f87171':'#334155'}}>
                      {Number(r.lost)>0 && <AlertTriangle size={12} style={{display:'inline',marginRight:4}} />}
                      {r.lost||0}
                    </td>
                    <td style={{padding:'8px 12px',color:'#fbbf24'}}>₾{Number(r.total_value||0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{borderTop:'2px solid #1e2d45',background:'#0f1826'}}>
                  <td colSpan={5} style={{padding:'9px 12px',fontSize:11,fontWeight:600,color:'#94a3b8'}}>სულ:</td>
                  <td style={{padding:'9px 12px',fontWeight:700,color:'#f1f5f9'}}>{items.reduce((s: number,i: any)=>s+Number(i.total_copies||0),0)}</td>
                  <td style={{padding:'9px 12px',fontWeight:700,color:'#34d399'}}>{items.reduce((s: number,i: any)=>s+Number(i.available||0),0)}</td>
                  <td style={{padding:'9px 12px',fontWeight:700,color:'#fbbf24'}}>{items.reduce((s: number,i: any)=>s+Number(i.checked_out||0),0)}</td>
                  <td style={{padding:'9px 12px',fontWeight:700,color:'#f87171'}}>{items.reduce((s: number,i: any)=>s+Number(i.lost||0),0)}</td>
                  <td style={{padding:'9px 12px',fontWeight:700,color:'#fbbf24'}}>₾{items.reduce((s: number,i: any)=>s+Number(i.total_value||0),0).toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
